import { z } from "zod";
/**
 * Who is in a workspace, who has been invited, and what each may do.
 *
 * Two roles are offered (plans/google-auth-and-invites.md, D2): an owner runs
 * the workspace and its people; a member does everything else. `admin` and
 * `viewer` exist in the database's enum and are never granted, so they are
 * readable here and not writable.
 */
export const grantableRoleSchema = z.enum(["owner", "member"]);
export const roleSchema = z.enum(["owner", "admin", "member", "viewer"]);
export const memberSchema = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    role: roleSchema,
    joinedAt: z.string(),
    isYou: z.boolean(),
});
/** An invite still waiting: not used, not revoked, not expired. */
export const pendingInviteSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: roleSchema,
    createdAt: z.string(),
    expiresAt: z.string(),
    /** Null when the person who sent it has since deleted their account. */
    invitedBy: z.string().nullable(),
});
export const membersListSchema = z.object({
    members: z.array(memberSchema),
    invites: z.array(pendingInviteSchema),
    /** Whether the person asking may invite, remove and change roles. */
    canManage: z.boolean(),
});
export const createInviteSchema = z.object({
    email: z.email(),
    role: grantableRoleSchema,
});
/** The link exists here and nowhere else: only its hash is stored. */
export const createdInviteSchema = z.object({
    invite: pendingInviteSchema,
    link: z.string(),
    /**
     * Whether the invite was emailed to the address. False when the mail could
     * not be sent: the invite still exists, and the link is there to copy.
     */
    emailed: z.boolean(),
});
export const setRoleSchema = z.object({ role: grantableRoleSchema });
/**
 * What the holder of a link may see before signing in.
 *
 * The address is masked: whoever has the link sees this, and it has to tell
 * the right person the invite is theirs without handing a stranger the
 * address.
 */
export const invitePreviewSchema = z.object({
    workspaceName: z.string(),
    invitedBy: z.string().nullable(),
    maskedEmail: z.string(),
    role: roleSchema,
    status: z.enum(["open", "expired", "revoked", "used"]),
});
/**
 * What opening an invite link did for someone already signed in
 * (`InviteAcceptance` in `members/schema.rs`; the Rust API only).
 */
export const inviteAcceptanceSchema = z.discriminatedUnion("outcome", [
    z.object({ outcome: z.literal("joined"), workspaceId: z.string() }),
    /** Signed in as someone else: nothing changed, the invite still works. */
    z.object({
        outcome: z.literal("wrong_email"),
        invitedMasked: z.string(),
        signedInAs: z.string(),
    }),
    /** Never issued, used by someone else, revoked or expired. */
    z.object({ outcome: z.literal("unusable") }),
]);
//# sourceMappingURL=members.js.map